import { useCallback, useEffect, useState } from 'react'
import { featureFlagsApi } from '../api/featureFlags'
import {
  RETENTION_CATEGORY_LABELS,
  RETENTION_EXECUTABLE,
  RETENTION_EXEC_FLAG,
  RETENTION_FLAG,
  retentionApi,
  type RetentionAction,
  type RetentionExecutionResult,
  type RetentionPolicy,
  type RetentionReportLine,
} from '../api/retention'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, TextField } from '../components/ui'
import { SelectField } from '../components/ui/SelectField'

/**
 * C-18: politiche di conservazione dei dati (solo Admin, flag `privacy.conservazione`). Mostra durata e azione per
 * categoria e un rapporto di cio' che scadrebbe. Non cancella ne' anonimizza nulla.
 */
export function RetentionPanel() {
  const [enabled, setEnabled] = useState(false)
  const [canExecute, setCanExecute] = useState(false)
  const [execCat, setExecCat] = useState<string | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [execNote, setExecNote] = useState('')
  const [execResult, setExecResult] = useState<RetentionExecutionResult | null>(null)
  const [policies, setPolicies] = useState<Record<string, RetentionPolicy>>({})
  const [categories, setCategories] = useState<string[]>([])
  const [draft, setDraft] = useState<Record<string, { days: string; action: RetentionAction }>>({})
  const [report, setReport] = useState<RetentionReportLine[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const r = await retentionApi.list()
    setCategories(r.categories)
    setPolicies(Object.fromEntries(r.items.map(p => [p.category, p])))
    setDraft(Object.fromEntries(r.categories.map(c => {
      const p = r.items.find(x => x.category === c)
      return [c, { days: p ? String(p.days) : '', action: p?.action ?? 'Anonimizza' }]
    })))
  }, [])

  useEffect(() => {
    featureFlagsApi.me()
      .then(r => {
        if (!r.flags[RETENTION_FLAG]) return
        setEnabled(true)
        setCanExecute(!!r.flags[RETENTION_EXEC_FLAG])
        return load()
      })
      .catch(() => { /* flag spento o utente non autorizzato: il pannello resta nascosto */ })
  }, [load])

  if (!enabled) return null

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try { await fn() } catch (e) { setError(e instanceof Error ? e.message : 'Operazione non riuscita') } finally { setBusy(false) }
  }

  const save = (c: string) => run(async () => {
    const d = draft[c]
    if (!d) return
    await retentionApi.save(c, Number(d.days), d.action)
    await load()
    setReport(null)
  })
  const remove = (c: string) => run(async () => { await retentionApi.remove(c); await load(); setReport(null) })
  const execute = (c: string) => run(async () => {
    const r = await retentionApi.execute(c, confirmText, execNote)
    setExecResult(r.result)
    setExecCat(null)
    setConfirmText('')
    setExecNote('')
    setReport((await retentionApi.report()).items)
  })
  function downloadReport() {
    if (report === null) return
    const rows = [['Categoria', 'Durata (giorni)', 'Azione', 'Scaduti', 'Nota'],
      ...report.map(l => [RETENTION_CATEGORY_LABELS[l.category] ?? l.category, l.days ?? '', l.action ?? '', l.candidates ?? '', l.note])]
    if (execResult) rows.push(['Eseguito', execResult.days, execResult.action, execResult.processed, `${execResult.batches} lotti, limite ${execResult.cutoff}`])
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(';')).join('\n')
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    a.download = 'rapporto-conservazione.csv'
    a.click()
    URL.revokeObjectURL(a.href)
  }
  const makeReport = () => run(async () => { setReport((await retentionApi.report()).items) })

  return (
    <Card variant="outlined" padding="none">
      <CardHeader title="Conservazione dei dati" icon="schedule" />
      <CardContent>
        <Callout tone="info">
          Nessuna cancellazione automatica: qui si impostano le durate e si vede cosa scadrebbe. Senza durata una categoria non scade mai.{canExecute ? ' L\'esecuzione e\' manuale, solo per i lead non convertiti, con conferma scritta e nota.' : ''}
        </Callout>
        {error !== null && <Callout tone="error">{error}</Callout>}
        <DataTable variant="plain">
          <tbody>
            {categories.map(c => (
              <tr key={c}>
                <td>{RETENTION_CATEGORY_LABELS[c] ?? c}</td>
                <td>
                  <TextField label="Giorni" type="number" min={30} max={36500}
                    value={draft[c]?.days ?? ''}
                    onChange={e => setDraft(p => ({ ...p, [c]: { action: p[c]?.action ?? 'Anonimizza', days: e.target.value } }))} />
                </td>
                <td>
                  <SelectField label="Azione" value={draft[c]?.action ?? 'Anonimizza'}
                    onChange={e => setDraft(p => ({ ...p, [c]: { days: p[c]?.days ?? '', action: e.target.value as RetentionAction } }))}>
                    <option value="Anonimizza">Anonimizza</option>
                    <option value="Cancella">Cancella</option>
                  </SelectField>
                </td>
                <td>
                  <Button variant="filled" size="sm" disabled={busy || !draft[c]?.days} onClick={() => void save(c)}>Salva</Button>
                  {policies[c] && <Button size="sm" variant="outlined" disabled={busy} onClick={() => void remove(c)}>Togli</Button>}
                  {canExecute && policies[c] && RETENTION_EXECUTABLE.includes(c) && (
                    <Button size="sm" variant="outlined" disabled={busy} onClick={() => { setExecCat(c); setConfirmText(''); setExecNote('') }}>Esegui</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
        <div className="mt-3">
          <Button variant="outlined" disabled={busy} onClick={() => void makeReport()}>Genera rapporto (sola lettura)</Button>
          {report !== null && <Button variant="outlined" onClick={downloadReport}>Scarica il rapporto</Button>}
        </div>
        {execCat !== null && (
          <div className="mt-3">
            <Callout tone="warning">
              Stai per anonimizzare in modo irreversibile i dati personali di «{RETENTION_CATEGORY_LABELS[execCat] ?? execCat}» scaduti. Genera prima il rapporto e controlla i numeri.
            </Callout>
            <TextField label="Scrivi CONFERMO" value={confirmText} onChange={e => setConfirmText(e.target.value)} />
            <TextField label="Nota obbligatoria (es. parere del consulente privacy del ...)" value={execNote} onChange={e => setExecNote(e.target.value)} />
            <Button variant="filled" disabled={busy || confirmText.trim() !== 'CONFERMO' || execNote.trim().length < 10} onClick={() => void execute(execCat)}>Esegui ora</Button>
            <Button variant="outlined" disabled={busy} onClick={() => setExecCat(null)}>Annulla</Button>
          </div>
        )}
        {execResult !== null && (
          <Callout tone="success">
            Eseguito: {execResult.processed} record trattati in {execResult.batches} lotti (limite {execResult.cutoff.slice(0, 10)}).
          </Callout>
        )}
        {report !== null && (
          <DataTable variant="plain">
            <thead><tr><th>Categoria</th><th>Durata</th><th>Azione</th><th>Scaduti</th><th>Nota</th></tr></thead>
            <tbody>
              {report.map(l => (
                <tr key={l.category}>
                  <td>{RETENTION_CATEGORY_LABELS[l.category] ?? l.category}</td>
                  <td>{l.days === null ? '—' : `${l.days} giorni`}</td>
                  <td>{l.action ?? '—'}</td>
                  <td>{l.candidates === null ? '—' : l.candidates}</td>
                  <td className="text-muted md-typescale-body-small">{l.note}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </CardContent>
    </Card>
  )
}
